import { useEffect, useReducer, useRef } from 'react';
import { Radio, Square } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { StreamingMarkdownText } from '@/components/streaming-markdown-text';
import { useStopLiveTranscription } from '@/hooks/mutations/use-stop-live-transcription';
import { useLiveTranscriptionState } from '@/hooks/queries/use-live-transcription-state';
import {
  formatLiveTranscript,
  initialLiveTranscriptionState,
  liveTranscriptionReducer,
} from '@/pages/workflow/helper/live-transcription-state';
import {
  LiveTranscriptEvent,
  NodeStatusEvent,
  WorkflowEventTypes,
} from '@/types/workflow-types';

interface LiveTranscriptionPanelProps {
  orgId?: string;
  workflowId?: string;
  sessionId?: string;
  nodeId: string;
  events: NodeStatusEvent[];
  isConnected: boolean;
  /**
   * The node has a begin/processing event in this run. Idle canvases and
   * nodes that have not started must not GET/poll.
   */
  enabled?: boolean;
  /**
   * This node has already finished. The panel still fetches once so a
   * finished run's transcript stays recoverable, but it must not keep
   * polling — a cancelled run's Redis checkpoint can sit at `active`
   * until TTL expiry.
   */
  isRunTerminal?: boolean;
}

export function LiveTranscriptionPanel({
  orgId,
  workflowId,
  sessionId,
  nodeId,
  events,
  isConnected,
  enabled = false,
  isRunTerminal = false,
}: LiveTranscriptionPanelProps) {
  const [state, dispatch] = useReducer(
    liveTranscriptionReducer,
    initialLiveTranscriptionState,
  );
  const wasConnectedRef = useRef(isConnected);
  const {
    data: snapshot,
    error,
    isFetching,
    refetch,
  } = useLiveTranscriptionState({
    orgId,
    workflowId,
    sessionId,
    nodeId,
    enabled,
    isRunTerminal,
  });
  const stopMutation = useStopLiveTranscription({
    orgId,
    workflowId,
    sessionId,
    nodeId,
  });
  useEffect(() => {
    if (snapshot) {
      dispatch({ type: 'hydrate', snapshot });
    }
  }, [snapshot]);

  useEffect(() => {
    if (error) {
      dispatch({ type: 'error', message: error.message });
    }
  }, [error]);

  useEffect(() => {
    if (stopMutation.error) {
      dispatch({
        type: 'stop_failed',
        message: stopMutation.error.message,
      });
    }
  }, [stopMutation.error]);

  useEffect(() => {
    events.forEach((event) => {
      if (event.event_type === WorkflowEventTypes.LIVE_TRANSCRIPT) {
        dispatch({ type: 'delta', event: event as LiveTranscriptEvent });
      }
    });
  }, [events]);

  useEffect(() => {
    // A finished run is never refetched on reconnect, so it must not be put
    // into `reconnecting` either -- it would have nothing to recover from.
    if (!isConnected && wasConnectedRef.current && !isRunTerminal) {
      dispatch({ type: 'reconnecting' });
    }
    if (isConnected && !wasConnectedRef.current && enabled && !isRunTerminal) {
      void refetch();
    }
    wasConnectedRef.current = isConnected;
  }, [isConnected, refetch, enabled, isRunTerminal]);

  const finalizedText = formatLiveTranscript(state);
  const isLiveStatus = state.status === 'active' || state.status === 'stopping';
  const isReconnecting =
    (isLiveStatus ||
      (state.status === 'loading' &&
        Boolean(finalizedText || state.interim))) &&
    (!isConnected || (isFetching && !snapshot));
  const canStop =
    !isRunTerminal &&
    (state.status === 'active' ||
      state.status === 'stopping' ||
      stopMutation.isPending);

  let statusLabel = 'Listening for speech';
  if (isReconnecting) statusLabel = 'Reconnecting';
  else if (state.status === 'stopping') statusLabel = 'Finalizing transcript';
  else if (state.status === 'final' || state.status === 'stopped')
    statusLabel = 'Transcription stopped';
  else if (state.status === 'cancelled')
    statusLabel = 'Transcription cancelled';
  else if (
    state.status === 'error' ||
    state.status === 'failed' ||
    state.status === 'inactive'
  )
    statusLabel = 'Transcription unavailable';
  else if (state.error) statusLabel = 'Stop failed';

  return (
    <section
      className='m-2 rounded-md border border-border bg-background/80 p-3'
      aria-label='Live transcription'
      aria-live='polite'
    >
      <div className='mb-2 flex items-center gap-2 text-xs font-medium text-muted-foreground'>
        <Radio className='size-3.5' aria-hidden='true' />
        <span>{statusLabel}</span>
        {canStop && (
          <Button
            type='button'
            variant='outline'
            size='sm'
            className='ml-auto h-7'
            disabled={state.status === 'stopping' || stopMutation.isPending}
            aria-label='Stop live transcription'
            onClick={() => {
              dispatch({ type: 'stop_requested' });
              stopMutation.mutate();
            }}
          >
            <Square className='size-3' aria-hidden='true' />
            {stopMutation.isPending ? 'Stopping…' : 'Stop'}
          </Button>
        )}
      </div>

      {/* The error sits above the transcript rather than replacing it. A
          failed Stop, or a checkpoint that has aged out of Redis while a
          past run is open, must not blank text the panel already holds --
          that text is the whole point of the node. */}
      {state.error && (
        <p className='mb-2 text-xs text-destructive'>{state.error}</p>
      )}

      {finalizedText ? (
        <StreamingMarkdownText
          content={finalizedText}
          isStreaming={state.status === 'active'}
          uniqueKey={`live-transcript-${nodeId}`}
          className='max-h-48 overflow-y-auto text-sm'
        />
      ) : (
        !state.error && (
          <p className='text-xs text-muted-foreground'>
            {isReconnecting
              ? 'Restoring the latest transcript…'
              : 'No speech detected yet.'}
          </p>
        )
      )}

      {state.interim?.text && (
        <p className='mt-2 border-t border-border pt-2 text-xs italic text-muted-foreground'>
          {state.interim.speaker ? `${state.interim.speaker}: ` : ''}
          {state.interim.text}
        </p>
      )}
    </section>
  );
}
